import React, { createContext, useContext, useState } from 'react';

import { FilterModalTypes } from '../../../../../components/FilterModal';
import { TableHeadItemOrderBy } from '../../../../../components/TableHead';
import { FiltersToApplyProps } from '../../../../../hooks/useFilterTransformer';
import { AssetDefaultOrSelectedTypeMap } from '../../../../../store/product/types';
import { AvailableProductsConstantsType, AvailableProductsType } from '../constants';

export interface GlobalProductContextReceivedData {
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  product: AvailableProductsConstantsType;
  infoGetter: <T extends AvailableProductsType>(
    offer: AssetDefaultOrSelectedTypeMap[T],
    idSelector: keyof AssetDefaultOrSelectedTypeMap[T]
  ) => {
    isSelected?: boolean;
    handleSelect?: (
      offer: AssetDefaultOrSelectedTypeMap[T],
      idSelector: keyof AssetDefaultOrSelectedTypeMap[T]
    ) => void;
  };
}

interface GlobalProductContextData extends GlobalProductContextReceivedData {
  orderBy: TableHeadItemOrderBy | undefined;
  setOrderBy: React.Dispatch<React.SetStateAction<TableHeadItemOrderBy | undefined>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  availableFilters: FilterModalTypes.FilterType[];
  setAvailableFilters: React.Dispatch<React.SetStateAction<FilterModalTypes.FilterType[]>>;
  filtersToApply: FiltersToApplyProps;
  setFiltersToApply: (filters: FiltersToApplyProps) => void;
  loadingCategories: boolean;
  setLoadingCategories: React.Dispatch<React.SetStateAction<boolean>>;
  assetCategories: any[];
  setProductCategories: React.Dispatch<React.SetStateAction<any[]>>;
  loadingProducts: boolean;
  setLoadingProducts: React.Dispatch<React.SetStateAction<boolean>>;
  currentRefresher: () => void;
  setCurrentRefresher: React.Dispatch<React.SetStateAction<() => void>>;
  loadingInitialProducts: boolean;
  loadingMoreProducts: boolean;
  hasSomeLoading: boolean;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

interface Props extends GlobalProductContextReceivedData {
  children: React.ReactNode;
}

const GlobalProductContext = createContext<GlobalProductContextData>(
  {} as GlobalProductContextData
);

const GlobalProductProvider: React.FC<Props> = ({ children, ...props }) => {
  const [orderBy, setOrderBy] = useState<TableHeadItemOrderBy>();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [assetCategories, setProductCategories] = useState<any[]>([]);
  const [availableFilters, setAvailableFilters] = useState<FilterModalTypes.FilterType[]>([]);
  const [filtersToApply, setFiltersToApplyState] = useState<FiltersToApplyProps>({});

  const [currentRefresher, setCurrentRefresher] = useState<() => void>(() => undefined);

  const loadingInitialProducts = loadingProducts && currentPage === 1;
  const loadingMoreProducts = loadingProducts && currentPage > 1;
  const hasSomeLoading = loadingProducts || loadingCategories;

  const setFiltersToApply = (filters: FiltersToApplyProps) => {
    setCurrentPage(1);
    setFiltersToApplyState(filters);
  };

  return (
    <GlobalProductContext.Provider
      value={{
        ...props,
        orderBy,
        setOrderBy,
        searchTerm,
        setSearchTerm,
        availableFilters,
        setAvailableFilters,
        filtersToApply,
        setFiltersToApply,
        loadingCategories,
        setLoadingCategories,
        assetCategories,
        setProductCategories,
        loadingProducts,
        setLoadingProducts,
        currentRefresher,
        setCurrentRefresher,
        loadingInitialProducts,
        loadingMoreProducts,
        hasSomeLoading,
        currentPage,
        setCurrentPage,
      }}>
      {children}
    </GlobalProductContext.Provider>
  );
};

const useGlobalProductContext = (): GlobalProductContextData => {
  const context = useContext(GlobalProductContext);

  if (!context) {
    throw new Error('useGlobalProductContext must be used within an GlobalProductContext');
  }
  return context;
};

export { GlobalProductProvider, useGlobalProductContext, GlobalProductContextData };
